import { getImpersonatedUserIdInLocalStorage } from '../../services/localStorage/localStorage';
import { ManagedLeadProductPath } from '../../services/api/managed-leads/managedLeads';

/**
 * Everything the managed lead pages put into React Query: the keys they read
 * and write, and the fetch policy those reads follow. An update has to
 * invalidate exactly the caches the list, detail and Notes pages read, and a key
 * spelled twice is a cache that silently stops refreshing.
 */
export const MANAGED_LEAD_QUERY_ROOT = 'managedLead';

/**
 * Who the cached leads belong to. `impersonatedUserId` is the only thing the
 * browser gets to choose about ownership — the exchange sends it and the
 * backend recomputes the effective owner from it — so it is exactly what a
 * cache entry must be scoped by. Without it, ending an impersonation would
 * serve the previous consultant's leads out of the cache while the refetch is
 * still in flight.
 */
export const managedLeadOwnerScope = () =>
  getImpersonatedUserIdInLocalStorage() ?? 'self';

/**
 * The fetch policy of every managed lead read, stated here rather than
 * inherited from the library's defaults. It says what the contract asks for and
 * nothing more: read again on entry, on a change of parameters, on returning to
 * the tab and on an explicit Riprova — and never on a timer.
 */
export const MANAGED_LEAD_QUERY_OPTIONS = {
  /** Riprova is the operator's, not the client's: a failure is reported once. */
  retry: false,
  staleTime: 0,
  refetchOnMount: true,
  /** Returning to the tab is a return to the page: the data may have moved on. */
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  /** No polling: the spec rules it out and this is where that is enforced. */
  refetchInterval: false as const,
};

export const managedLeadListQueryKey = (product: ManagedLeadProductPath) => [
  MANAGED_LEAD_QUERY_ROOT,
  'list',
  product,
  managedLeadOwnerScope(),
];

export const managedLeadDetailQueryKey = (
  product: ManagedLeadProductPath,
  dealId: string
) => [
  MANAGED_LEAD_QUERY_ROOT,
  'detail',
  product,
  managedLeadOwnerScope(),
  dealId,
];

export const managedLeadNotesQueryKey = (
  product: ManagedLeadProductPath,
  dealId: string
) => [
  MANAGED_LEAD_QUERY_ROOT,
  'notes',
  product,
  managedLeadOwnerScope(),
  dealId,
];

/**
 * The lifecycle policy of a product. Every page that has to turn a stage id
 * into a label reads this one cache instead of fetching the policy again. It is
 * versioned policy, the same for every operator, so it is the one managed cache
 * the owner does not scope.
 */
export const managedLeadLifecycleQueryKey = (
  product: ManagedLeadProductPath
) => [MANAGED_LEAD_QUERY_ROOT, 'lifecycle', product];

/**
 * The administrative Drop queue of a product. It is not one of the consultant's
 * caches: an approval moves the lead, so it invalidates the lists and the
 * detail as well as the queue it was decided from. It is still read with the
 * effective owner's token, so it is scoped like the rest.
 */
export const managedDropReviewQueryKey = (product: ManagedLeadProductPath) => [
  MANAGED_LEAD_QUERY_ROOT,
  'dropReview',
  product,
  managedLeadOwnerScope(),
];
